import { useEffect } from "react";

import { find, isEmpty } from "lodash-es";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import slugify from "slugify";

import { Flow } from "src/api/flowTypes";
import { useFlowVersion } from "src/api/flowVersionQueries";
import { useCopyFlow, useFlows, useSourceScopeToken } from "src/api/queries";
import { Input } from "src/base-components/Input";
import {
  LoadingView,
  LoadingViewQueryResult,
} from "src/base-components/LoadingView";
import { RequiredAsterisk } from "src/base-components/RequiredAsterisk";
import { Button } from "src/design-system/Button";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { FlowAndVersionDropdown } from "src/flowsOverview/FlowAndVersionDropdown";
import { NameInput } from "src/flowsOverview/FlowNameInput";
import { SlugInput } from "src/flowsOverview/FlowSlugInput";
import { OrgAndWorkspaceDropdown } from "src/flowsOverview/OrgAndWorkspaceDropdown";
import { FlowFromTemplateForm } from "src/flowsOverview/types";
import { handleNoFolderId } from "src/flowsOverview/v2/handleNoFolderId";
import { OrgWithWorkspaces } from "src/layout/types";
import { nameValidations } from "src/layout/WorkspaceFormModal/utils";
import { useFolderIdSearchParam } from "src/router/SearchParams";
import { DashboardPageParams } from "src/router/urls";
import { wrapWithAxiosResponseErrorHandler } from "src/utils/toastError";
import { useNavigableWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";
import { useParamsDecode } from "src/utils/useParamsDecode";
import {
  FLOW_SLUG_CONSTRAINTS,
  getStringValidations,
} from "src/utils/validations";

type Props = {
  isOpen: boolean;
  handleClose: () => void;
};

type SelectFlowAndVersionProps = {
  flowsResult: LoadingViewQueryResult<Flow[]>;
} & { index: number | null };

const SelectFlowAndVersion: React.FC<SelectFlowAndVersionProps> = ({
  flowsResult,
  index,
}) => {
  const { control, setValue } = useFormContext<FlowFromTemplateForm>();

  return (
    <LoadingView
      queryResult={flowsResult}
      renderUpdated={(flows: Flow[]) => (
        <div className="mb-6">
          <p className="mb-2 font-inter-semibold-13px text-gray-800">
            Create from <RequiredAsterisk />
          </p>
          {index !== null ? (
            <Controller
              control={control}
              name={`childFlows.${index}`}
              render={({ field }) => {
                const selectedFlow = flows.find(
                  (flow) => flow.id === field.value.id,
                );
                return (
                  <Input
                    value={`${selectedFlow?.name} - ${
                      field.value.versionIds.length
                    } version${field.value.versionIds.length > 1 ? "s" : ""}`}
                    disabled
                    fullWidth
                  />
                );
              }}
            />
          ) : (
            <Controller
              control={control}
              name="flowToCopy"
              render={({ field }) => (
                <FlowAndVersionDropdown
                  flows={flows}
                  selected={field.value}
                  onSelect={(key) => {
                    field.onChange(key);
                    const selectedFlow = find(flows, ["id", key?.id]);
                    if (selectedFlow) {
                      setValue("name", selectedFlow.name);
                      setValue("slug", selectedFlow.slug);
                    }
                  }}
                />
              )}
            />
          )}
        </div>
      )}
    />
  );
};

const SelectOrgAndWorkspace: React.FC<{
  orgs: OrgWithWorkspaces[];
}> = ({ orgs }) => {
  const { control, resetField } = useFormContext<FlowFromTemplateForm>();
  return (
    <div className="mb-6">
      <p className="mb-2 font-inter-semibold-13px text-gray-800">
        Select org and workspace <RequiredAsterisk />
      </p>
      <Controller
        control={control}
        name="orgWorkspace"
        render={({ field }) => (
          <OrgAndWorkspaceDropdown
            orgs={orgs}
            selected={field.value}
            onSelect={(value) => {
              field.onChange(value);
              // reset the flow fields when selecting a new workspace
              if (value?.workspaceId !== field.value.workspaceId) {
                resetField("flowToCopy");
                resetField("name");
                resetField("slug");
                resetField("childFlows");
              }
            }}
          />
        )}
      />
    </div>
  );
};

export const CreateFlowFromTemplateModal: React.FC<Props> = ({
  isOpen,
  handleClose,
}) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const [folderId] = useFolderIdSearchParam();
  const copyFlowMutation = useCopyFlow(wsId);
  const formMethods = useForm<FlowFromTemplateForm>({
    defaultValues: {
      orgWorkspace: { orgId: undefined, workspaceId: undefined },
      flowToCopy: { id: undefined, versionId: undefined },
      childFlows: [],
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const {
    formState: { errors, isSubmitting },
    control,
    reset,
    register,
    getValues,
    setValue,
    handleSubmit,
  } = formMethods;
  const { fields } = useFieldArray({
    control: control,
    name: "childFlows",
    keyName: "fieldId",
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const { orgs } = useNavigableWorkspaceAndOrg();
  const selectedOrgId = useWatch({ control, name: "orgWorkspace.orgId" });
  const selectedWorkspaceId = useWatch({
    control,
    name: "orgWorkspace.workspaceId",
  });

  const isCrossWorkspace =
    selectedWorkspaceId !== undefined && selectedWorkspaceId !== wsId;
  const sourceTokenQuery = useSourceScopeToken(
    isCrossWorkspace ? selectedOrgId : undefined,
    isCrossWorkspace ? selectedWorkspaceId : undefined,
  );
  const sourceToken = sourceTokenQuery.data;
  const sourceReady = !isCrossWorkspace || !!sourceToken;

  const flowsResult = useFlows({
    workspaceId: selectedWorkspaceId,
    disabled: selectedWorkspaceId === undefined || !sourceReady,
    authToken: sourceToken,
  });

  const flowsResultWithScope: LoadingViewQueryResult<Flow[]> = {
    ...flowsResult,
    isLoading:
      flowsResult.isLoading || (isCrossWorkspace && sourceTokenQuery.isLoading),
    isError:
      flowsResult.isError || (isCrossWorkspace && sourceTokenQuery.isError),
    error: flowsResult.error ?? (sourceTokenQuery.error as Error | null),
  };

  const selectedVersionId = useWatch({
    control,
    name: "flowToCopy.versionId",
  });
  const flowVersion = useFlowVersion(
    sourceReady ? selectedVersionId : undefined,
    false,
    sourceToken,
  );

  const onSubmit = wrapWithAxiosResponseErrorHandler(
    handleSubmit(async (formData) => {
      await copyFlowMutation.mutateAsync({
        sourceOrgId: formData.orgWorkspace.orgId,
        flowId: formData.flowToCopy.id,
        flowVersionId: formData.flowToCopy.versionId,
        newName: formData.name,
        newSlug: formData.slug,
        childFlowNamings: Object.fromEntries(
          formData.childFlows.map((childFlow) => [
            childFlow.id,
            { name: childFlow.name, slug: childFlow.slug },
          ]),
        ),
        folderId: handleNoFolderId(folderId),
      });
      toastActions.success({
        title: "Decision Flow created",
        description:
          "The Decision Flow has been successfully created from the template.",
      });
      handleClose();
    }),
  );

  // When a template is selected we need to find its child flows and set them into the form
  useEffect(() => {
    if (
      flowVersion.data &&
      flowsResult.data &&
      flowVersion.data.child_versions
    ) {
      const currentChildFlows = getValues("childFlows");
      const childFlows = flowVersion.data.child_versions.reduce<
        FlowFromTemplateForm["childFlows"]
      >((acc, childVersion) => {
        const childFlow = flowsResult.data?.find((flow) =>
          flow.versions.some((version) => version.id === childVersion.id),
        );
        const alreadyFoundChild = acc.find(
          (childFlowFound) => childFlowFound.id === childFlow?.id,
        );
        if (alreadyFoundChild) {
          alreadyFoundChild.versionIds.push(childVersion.id);
          return acc;
        }
        // This effect re-runs whenever another page of flows arrives or the
        // version poll refreshes, so it must preserve names and slugs already
        // typed into the form instead of resetting them to the source values.
        // A row whose flow has not been loaded yet is stored with id "", so
        // typed values are matched by versionId, which is known from the start.
        const typedChildFlow = currentChildFlows.find(
          (childFlowFound) =>
            childFlowFound.versionIds.includes(childVersion.id) ||
            childFlowFound.id === childFlow?.id,
        );
        return [
          ...acc,
          {
            id: childFlow?.id ?? "",
            versionIds: [childVersion.id],
            name: typedChildFlow?.name || childFlow?.name || "",
            slug: typedChildFlow?.slug || childFlow?.slug || "",
          },
        ];
      }, []);
      setValue("childFlows", childFlows);
    }
  }, [flowVersion.data, flowsResult.data, getValues, setValue]);

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Modal.Header description="Choose the Decision Flow and version you want to duplicate">
        Create Decision Flow from a template
      </Modal.Header>
      <form>
        <Modal.Content>
          <FormProvider {...formMethods}>
            <SelectOrgAndWorkspace orgs={orgs} />
            <SelectFlowAndVersion
              flowsResult={flowsResultWithScope}
              index={null}
            />
            <NameInput
              error={errors.name}
              formProps={register("name", {
                required: true,
                validate: nameValidations,
                onChange: (e) => {
                  setValue(
                    "slug",
                    slugify(e.target.value, { strict: true, lower: true }),
                    {
                      shouldValidate: true,
                    },
                  );
                },
              })}
            />
            <SlugInput
              error={errors.slug}
              formProps={register("slug", {
                required: true,
                validate: getStringValidations(FLOW_SLUG_CONSTRAINTS),
              })}
            />
            {fields.length > 0 && (
              <div className="border-gray-200 border-t pt-6">
                {fields.map((field, index) => (
                  <div key={field.id}>
                    <p className="pb-3 font-inter-normal-13px text-gray-600">
                      Child flow {index + 1}
                    </p>
                    <SelectFlowAndVersion
                      flowsResult={flowsResultWithScope}
                      index={index}
                    />
                    <NameInput
                      dataLoc={`add-flow-name-child-${index}`}
                      error={errors.childFlows?.[index]?.name}
                      formProps={register(`childFlows.${index}.name`, {
                        required: true,
                        validate: nameValidations,
                        onChange: (e) => {
                          setValue(
                            `childFlows.${index}.slug`,
                            slugify(e.target.value, {
                              strict: true,
                              lower: true,
                            }),
                            {
                              shouldValidate: true,
                            },
                          );
                        },
                      })}
                    />
                    <SlugInput
                      dataLoc={`add-flow-slug-child-${index}`}
                      error={errors.childFlows?.[index]?.slug}
                      formProps={register(`childFlows.${index}.slug`, {
                        required: true,
                        validate: getStringValidations(FLOW_SLUG_CONSTRAINTS),
                      })}
                    />
                  </div>
                ))}
              </div>
            )}
          </FormProvider>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="add-flow-save"
              isDisabled={!isEmpty(errors)}
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
              onClick={onSubmit}
            >
              Create
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
